import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  EditOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PersonalInfoSkeleton from "../components/PersonalInfoSkeleton";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

const Container = styled.div`
  /* min-height: 100vh; */
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  height: 60vh;
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  color: #000;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    color: #666;
  }
`;

const Section = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 20px 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;

  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const InfoLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #666;
  min-width: 120px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #000;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #333;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmptyMessage = styled.p`
  font-size: 13px;
  color: #ff6b6b;
  margin: 0;
  font-weight: 500;
`;

const EditButton = styled.button`
  padding: 12px 24px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #333;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

// Modal Styles
const Modal = styled.div`
  display: ${(props) => (props.open ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ModalHeader = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }

  input {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    transition: all 0.3s;

    &:focus {
      outline: none;
      border-color: #000;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #ff6b6b;
  margin-top: 4px;
`;

const SuccessMessage = styled.span`
  font-size: 12px;
  color: #51cf66;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  button {
    flex: 1;
    padding: 12px 24px;
    border-radius: 6px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }
`;

const PrimaryButton = styled.button`
  background: #000;
  color: #fff;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: #f0f0f0;
  color: #000;
  border: 1px solid #ddd;

  &:hover {
    background: #e0e0e0;
  }
`;

const OTPContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OTPInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  font-family: monospace;
  text-align: center;
  letter-spacing: 8px;
  font-weight: 700;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
`;

const ResendGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ResendButton = styled.button`
  padding: 8px 16px;
  background: #f0f0f0;
  color: #000;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: #e0e0e0;
    border-color: #ccc;
  }

  &:disabled {
    background: #f9f9f9;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const TimerText = styled.span`
  font-size: 12px;
  color: #999;
`;

const VerifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #51cf66;
  font-size: 12px;
  font-weight: 600;
`;

const EditEmailButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
`;

// Validation function
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const PersonalInfo = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [activeModal, setActiveModal] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const navigate = useNavigate();
  const token = useSelector((state) => state.token);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!token) {
          navigate("/login");
          return;
        }
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/auth/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const data = await resp.json();
        if (data.status && data.data && data.data.user) {
          const user = data.data.user;
          setUserData({
            firstName: user.name?.split(" ")?.[0] || "",
            lastName: user.name?.split(" ")?.[1] || "",
            email: user.email || "",
            phone: user.phone || "",
          });
          // Check if email is verified
          if (user.email) {
            setEmailVerified(true);
          }
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token, navigate]);

  // Resend OTP timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleOpenEmailModal = (isEditing = false) => {
    setIsEditingEmail(isEditing);
    if (!isEditing) {
      setEmailInput("");
    }
    setEmailError("");
    setEmailSuccess("");
    setOtpSent(false);
    setOtpInput("");
    setResendTimer(0);
    setActiveModal("email");
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setEmailInput("");
    setEmailError("");
    setEmailSuccess("");
    setOtpInput("");
    setOtpError("");
    setOtpSent(false);
    setResendTimer(0);
  };

  // Handle email input and request OTP
  const handleEmailSubmit = () => {
    setEmailError("");
    setEmailSuccess("");

    if (!emailInput.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(emailInput)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Simulate API call to request OTP

    setEmailSuccess("OTP sent to your email!");
    setOtpSent(true);
    setResendTimer(60);
  };

  // Handle OTP verification
  const handleOTPSubmit = () => {
    setOtpError("");

    if (!otpInput.trim()) {
      setOtpError("OTP is required");
      return;
    }

    if (otpInput.length !== 4) {
      setOtpError("OTP must be 4 digits");
      return;
    }

    // Simulate OTP verification
    if (!isNaN(otpInput)) {
      // Update user data with verified email
      setUserData((prev) => ({ ...prev, email: emailInput }));
      setEmailVerified(true);
      handleCloseModal();
    } else {
      setOtpError("OTP must contain only numbers");
    }
  };

  // Handle resend OTP
  const handleResendOTP = () => {
    setOtpInput("");
    setOtpError("");
    setResendTimer(60);
  };

  if (loading) {
    return (
      <>
        {/* <Navbar /> */}
        <PersonalInfoSkeleton />
        <Footer />
      </>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <Wrapper>
        <Header>
          <BackButton onClick={() => navigate("/my-account")}>
            <ArrowLeftOutlined />
          </BackButton>
          <PageTitle>Personal Information</PageTitle>
        </Header>

        {/* Personal Details Section */}
        <Section>
          <SectionTitle>Your Details</SectionTitle>

          <InfoRow>
            <InfoLabel>First Name</InfoLabel>
            <InfoValue>{userData.firstName}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Last Name</InfoLabel>
            <InfoValue>{userData.lastName}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Phone Number</InfoLabel>
            <InfoValue>{userData.phone}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>
              {userData.email ? (
                <>
                  {userData.email}
                  {emailVerified && (
                    <>
                      <VerifiedBadge>
                        <CheckCircleOutlined /> Verified
                      </VerifiedBadge>
                      <EditEmailButton
                        onClick={() => handleOpenEmailModal(true)}
                        title="Edit email"
                      >
                        <EditOutlined />
                      </EditEmailButton>
                    </>
                  )}
                </>
              ) : (
                <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                  <EmptyMessage>
                    Add email to get updates and secure your account
                  </EmptyMessage>
                  <AddButton onClick={() => handleOpenEmailModal(false)}>
                    Add Email
                  </AddButton>
                </div>
              )}
            </InfoValue>
          </InfoRow>
        </Section>
      </Wrapper>

      {/* Add/Edit Email Modal */}
      <Modal open={activeModal === "email"}>
        <ModalContent>
          <ModalHeader>
            <span>
              {isEditingEmail ? "Change Email Address" : "Add Email Address"}
            </span>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>

          {!otpSent ? (
            <>
              <FormGroup>
                <label>Email Address</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError("");
                  }}
                  placeholder="Enter your email address"
                  disabled={otpSent}
                />
                {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
                {emailSuccess && (
                  <SuccessMessage>{emailSuccess}</SuccessMessage>
                )}
              </FormGroup>

              <ButtonGroup>
                <PrimaryButton
                  onClick={handleEmailSubmit}
                  disabled={otpSent || !emailInput.trim()}
                >
                  Send OTP
                </PrimaryButton>
                <SecondaryButton onClick={handleCloseModal}>
                  Cancel
                </SecondaryButton>
              </ButtonGroup>
            </>
          ) : (
            <>
              <p
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginBottom: "20px",
                }}
              >
                Enter the 4-digit OTP sent to <strong>{emailInput}</strong>
              </p>

              <OTPContainer>
                <FormGroup>
                  <label>Enter OTP</label>
                  <OTPInput
                    type="text"
                    value={otpInput}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 4);
                      setOtpInput(value);
                      setOtpError("");
                    }}
                    placeholder="0000"
                    maxLength="4"
                    inputMode="numeric"
                  />
                  {otpError && <ErrorMessage>{otpError}</ErrorMessage>}
                </FormGroup>

                <ResendGroup>
                  <TimerText>
                    {resendTimer > 0
                      ? `Resend available in ${resendTimer}s`
                      : "Didn't receive OTP?"}
                  </TimerText>
                  <ResendButton
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0}
                  >
                    Resend
                  </ResendButton>
                </ResendGroup>
              </OTPContainer>

              <ButtonGroup>
                <PrimaryButton
                  onClick={handleOTPSubmit}
                  disabled={!otpInput.trim()}
                >
                  Verify & Add Email
                </PrimaryButton>
                <SecondaryButton onClick={handleCloseModal}>
                  Cancel
                </SecondaryButton>
              </ButtonGroup>
            </>
          )}
        </ModalContent>
      </Modal>

      <Footer />
    </Container>
  );
};

export default PersonalInfo;
